/**
 * Ajuste 12 (puro): miniatura / tipo de cada anuncio y la respuesta de
 * `GET /getmetaadpreview`. La URL de la vista previa vence: se pide cada vez que
 * se abre el diálogo y nunca se guarda (ni estado global ni localStorage).
 * Ver docs/maps/meta-ads.md § Ver anuncio.
 */
import { stripMetaTerrioMark } from './metaFormat';

export const META_AD_PREVIEW_DEFAULT_FORMAT = 'MOBILE_FEED_STANDARD';
/** Sin `onLoad` del iframe en este tiempo → fallback a la miniatura. */
export const META_AD_PREVIEW_TIMEOUT_MS = 8000;
export const META_AD_PREVIEW_DEFAULT_SIZE = { width: 540, height: 690 };

const CREATIVE_KINDS = ['video', 'image', 'carousel', 'other'];

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const parseUrl = (value) => {
  const s = str(value);
  if (!s) return null;
  try {
    return new URL(s);
  } catch (_e) {
    return null;
  }
};

/** Solo `https://` (la miniatura de Meta vence y puede no cargar: igual se valida). */
export function metaHttpsUrl(value) {
  const url = parseUrl(value);
  return url && url.protocol === 'https:' ? url.href : null;
}

const hostIs = (host, domain) => host === domain || host.endsWith(`.${domain}`);

/** La API ya valida (https en facebook.com / fb.com); el Front lo vuelve a chequear. */
export function metaPreviewUrlAllowed(value) {
  const url = parseUrl(value);
  if (!url || url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  return hostIs(host, 'facebook.com') || hostIs(host, 'fb.com');
}

/** Campos del ajuste 12 en cada anuncio (getmetacampaigns / getunitcampaigns / getcampaignmetrics). */
export function readMetaAdCreative(raw) {
  const kind = CREATIVE_KINDS.includes(raw?.creative_kind) ? raw.creative_kind : null;
  return {
    thumbnailUrl: metaHttpsUrl(raw?.thumbnail_url),
    thumbnailFetchedAt: str(raw?.thumbnail_fetched_at),
    creativeKind: kind,
    isVideo: raw?.is_video === true || kind === 'video',
  };
}

const size = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
};

/**
 * `data` de getmetaadpreview. `available` solo si además la URL pasa la validación.
 *
 * @returns {{ adId: string|null, adName: string|null, format: string, formatLabel: string|null,
 *   available: boolean, unavailableReason: string|null, previewUrl: string|null, width: number,
 *   height: number, expiresHint: string|null, formats: Array<{ key: string, label: string }>,
 *   thumbnailUrl: string|null, isVideo: boolean }}
 */
export function normalizeMetaAdPreview(data) {
  const d = data && typeof data === 'object' ? data : {};
  const urlOk = metaPreviewUrlAllowed(d.preview_url);
  const available = d.available === true && urlOk;
  const creative = readMetaAdCreative(d);
  const formats = (Array.isArray(d.formats) ? d.formats : [])
    .map((f) => ({ key: str(f?.key), label: str(f?.label) || str(f?.key) }))
    .filter((f) => f.key);
  return {
    adId: str(d.ad_id == null ? '' : String(d.ad_id)),
    adName: str(stripMetaTerrioMark(d.ad_name)),
    format: str(d.ad_format) || META_AD_PREVIEW_DEFAULT_FORMAT,
    formatLabel: str(d.ad_format_label),
    available,
    unavailableReason: available ? null : (str(d.unavailable_reason) || (d.available === true ? 'INVALID_PREVIEW' : null)),
    previewUrl: available ? new URL(d.preview_url.trim()).href : null,
    width: size(d.width, META_AD_PREVIEW_DEFAULT_SIZE.width),
    height: size(d.height, META_AD_PREVIEW_DEFAULT_SIZE.height),
    expiresHint: str(d.expires_hint),
    formats,
    thumbnailUrl: creative.thumbnailUrl,
    isVideo: creative.isVideo,
  };
}

/** Detalle del fallback: error de la API, sin vista previa o el iframe que no cargó. */
export function metaAdPreviewFallbackDetail({ error = null, reason = null, timedOut = false } = {}) {
  if (error) return error;
  if (timedOut) return 'Meta tardó más de 8 segundos en mostrarla.';
  if (reason === 'NO_PREVIEW') return 'Meta no tiene vista previa de este anuncio en este formato. Probá con otro formato.';
  if (reason === 'INVALID_PREVIEW') return 'Meta mandó una vista previa que no podemos mostrar.';
  return 'Meta no mandó la vista previa.';
}
